import _ from 'lodash'
import { useId, useState } from 'react'
import { match } from 'ts-pattern'
import { z } from 'zod'
import type { DismissedBy, DismissedLead } from '../../../utils/api-client/api-schemas.ts'
import { pluralize } from '../../../utils/text-utils.ts'
import { RenderedMarkdown } from '../../rendered-markdown.tsx'
import { lensLabel } from '../beacon-run.ts'
import * as styles from './beacon-dismissed-leads.css.ts'
import {
  type CoverageEntity,
  type CoverageModel,
  dismissedLeadKey,
  KIND_LABELS,
  unlistedDismissedCount,
} from './coverage-model.ts'

/** The section's anchor, so the verdict, the evidence and the detail panel can link down to it. */
export const BEACON_DISMISSED_LEADS_ID = 'beacon-dismissed-leads'

/** One lead's anchor inside the section. */
export const dismissedLeadAnchor = (lead: Pick<DismissedLead, 'finding_uuid'>) =>
  `beacon-dismissed-lead-${lead.finding_uuid}`

export const DISMISSED_BY_LABELS: Record<DismissedBy, string> = {
  verifier: 'Refuted by verifier',
  triage: 'Triage: false positive',
}

export const DismissedLeadGroupingSchema = z.enum(['where', 'weakness', 'dismissed-by'])
export type DismissedLeadGrouping = z.infer<typeof DismissedLeadGroupingSchema>

const GROUPING_LABELS: Record<DismissedLeadGrouping, string> = {
  where: 'Where',
  weakness: 'Weakness',
  'dismissed-by': 'Who dismissed',
}

type DismissedByFilter = DismissedBy | 'all'

export type DismissedLeadGroup = { key: string; eyebrow?: string; title: string; leads: DismissedLead[] }

/** The part a lead would have been placed on, or the repository when the summary names a part it lacks. */
export const placedEntity = (model: CoverageModel, lead: DismissedLead): CoverageEntity =>
  model.entities.get(dismissedLeadKey(lead)) ?? model.repository

/** The entry point an operation or group sits under; any other part stands for itself. */
const landingEntity = (model: CoverageModel, entity: CoverageEntity): CoverageEntity => {
  const entryPointKey = model.operations.get(entity.key)?.entryPointKey ?? model.groups.get(entity.key)?.entryPointKey
  return (entryPointKey ? model.entities.get(entryPointKey) : undefined) ?? entity
}

/**
 * The leads in groups. By where they would have landed, one group per entry point (operations and groups
 * fold into theirs), control or the repository, in the threat model's order; by weakness or by who
 * dismissed them, largest group first. Inside a group the leads keep the summary's order.
 */
export const groupDismissedLeads = (
  model: CoverageModel,
  leads: readonly DismissedLead[],
  grouping: DismissedLeadGrouping
): DismissedLeadGroup[] =>
  match(grouping)
    .with('where', () => {
      const order = [...model.entities.keys()]
      const byLanding = _.groupBy(leads, lead => landingEntity(model, placedEntity(model, lead)).key)
      return _.sortBy(Object.keys(byLanding), key => order.indexOf(key)).map((key): DismissedLeadGroup => {
        const entity = model.entities.get(key) ?? model.repository
        return { key, eyebrow: KIND_LABELS[entity.kind], title: entity.name, leads: byLanding[key] ?? [] }
      })
    })
    .with('weakness', () =>
      _.sortBy(
        Object.entries(_.groupBy(leads, lead => lead.weakness)).map(([weakness, members]): DismissedLeadGroup => ({
          key: weakness,
          title: weakness,
          leads: members,
        })),
        group => -group.leads.length
      )
    )
    .with('dismissed-by', () =>
      (['verifier', 'triage'] as const)
        .map((judge): DismissedLeadGroup => ({
          key: judge,
          title: DISMISSED_BY_LABELS[judge],
          leads: leads.filter(lead => lead.dismissed_by === judge),
        }))
        .filter(group => group.leads.length > 0)
    )
    .exhaustive()

/** Where a lead would have landed, in words, when its group does not already say so. */
const whereLabel = (model: CoverageModel, lead: DismissedLead, grouping: DismissedLeadGrouping) => {
  const placed = placedEntity(model, lead)
  const landing = landingEntity(model, placed)
  if (grouping === 'where') {
    return placed === landing ? undefined : `${KIND_LABELS[placed.kind]}: ${placed.name}`
  }
  return placed === landing ? `${KIND_LABELS[placed.kind]}: ${placed.name}` : `${landing.name} › ${placed.name}`
}

const formatLines = (lead: DismissedLead) =>
  lead.line_end > lead.line_start
    ? `${lead.file_path}:${lead.line_start}–${lead.line_end}`
    : `${lead.file_path}:${lead.line_start}`

/** Plain text with `code` spans set as code; the explanation is prose, the details are full markdown. */
const ProseWithCode: React.FC<{ text: string }> = ({ text }) => (
  <>
    {text.split(/`([^`]+)`/).map((part, index) =>
      index % 2 === 1 ? (
        <code key={index} className={styles.inlineCode}>
          {part}
        </code>
      ) : (
        part
      )
    )}
  </>
)

/** The details without a first paragraph that only repeats the explanation already shown above them. */
const detailsBeyondExplanation = ({ details, explanation }: Pick<DismissedLead, 'details' | 'explanation'>) => {
  const body = details.trimStart()
  const repeated = explanation.trim()
  const rest = body.slice(repeated.length)
  return repeated && body.startsWith(repeated) && (rest === '' || rest.startsWith('\n')) ? rest.trimStart() : details
}

const DismissedLeadItem: React.FC<{
  lead: DismissedLead
  where?: string
  isOpen: boolean
  onToggle: () => unknown
}> = ({ lead, where, isOpen, onToggle }) => {
  const detailsId = useId()
  const titleId = useId()
  const details = detailsBeyondExplanation(lead)
  return (
    <li id={dismissedLeadAnchor(lead)} className={styles.lead} aria-labelledby={titleId}>
      <div className={styles.leadHead}>
        <h5 id={titleId} className={styles.leadTitle}>
          {lead.title}
        </h5>
        <span className={styles.judge({ dismissedBy: lead.dismissed_by })}>
          {DISMISSED_BY_LABELS[lead.dismissed_by]}
        </span>
      </div>
      <div className={styles.meta}>
        <code className={styles.location}>{formatLines(lead)}</code>
        <span className={styles.chip}>{lead.weakness}</span>
        {lead.cwe_ids.map(cwe => (
          <span key={cwe} className={styles.cweChip}>
            {cwe}
          </span>
        ))}
        <span>Found by the {lensLabel(lead.lens)} lens</span>
        {where && <span>· {where}</span>}
      </div>
      {lead.explanation && (
        <p className={styles.explanation}>
          <ProseWithCode text={lead.explanation} />
        </p>
      )}
      {details && (
        <>
          <button
            type="button"
            className={styles.disclosure}
            aria-expanded={isOpen}
            aria-controls={detailsId}
            onClick={onToggle}
          >
            {isOpen ? 'Hide full details' : 'Show full details'}
          </button>
          {isOpen && (
            <div id={detailsId} className={styles.details} role="region" aria-label={`Details: ${lead.title}`}>
              <RenderedMarkdown markdown={details} />
            </div>
          )}
        </>
      )}
    </li>
  )
}

export type BeaconDismissedLeadsProps = {
  model: CoverageModel
  /** Raised to the top of a run with no verified findings, where these are the run's whole result. */
  isProminent?: boolean
  initialGrouping?: DismissedLeadGrouping
  /** Leads whose details start open, for stories and tests. */
  initiallyOpen?: readonly string[]
}

/**
 * Every suspected weakness Beacon raised and then ruled out, and why: the verifier refuted it, or triage
 * judged it a false positive. Grouped by where each would have landed on the attack surface, by weakness,
 * or by who dismissed it, each with its explanation and the full report behind it.
 */
export const BeaconDismissedLeads: React.FC<BeaconDismissedLeadsProps> = ({
  model,
  isProminent = false,
  initialGrouping = 'where',
  initiallyOpen = [],
}) => {
  const titleId = useId()
  const leads = model.summary.dismissed
  const unlisted = unlistedDismissedCount(model.summary)
  const [grouping, setGrouping] = useState<DismissedLeadGrouping>(initialGrouping)
  const [filter, setFilter] = useState<DismissedByFilter>('all')
  const [open, setOpen] = useState<ReadonlySet<string>>(() => new Set(initiallyOpen))
  const counts = _.countBy(leads, lead => lead.dismissed_by)
  const visible = filter === 'all' ? leads : leads.filter(lead => lead.dismissed_by === filter)
  const toggle = (uuid: string) =>
    setOpen(previous => {
      const next = new Set(previous)
      if (!next.delete(uuid)) {
        next.add(uuid)
      }
      return next
    })

  return (
    <section
      id={BEACON_DISMISSED_LEADS_ID}
      className={styles.section({ isProminent })}
      aria-labelledby={titleId}
      tabIndex={-1}
    >
      <header className={styles.header}>
        <div className={styles.titleRow}>
          <h3 id={titleId} className={styles.title}>
            Leads Beacon dismissed
          </h3>
          <span className={styles.total}>{leads.length}</span>
        </div>
        {leads.length > 0 && (
          <p className={styles.intro}>
            Suspected weaknesses Beacon raised and then ruled out. None of them is among the run’s findings; each says
            why it was dismissed.
          </p>
        )}
      </header>
      {leads.length === 0 ? (
        <p className={styles.empty}>
          {unlisted > 0
            ? `Beacon dismissed ${unlisted} ${pluralize(unlisted, 'lead', 'leads')}, but this run’s summary does not list them.`
            : 'Beacon dismissed no leads.'}
        </p>
      ) : (
        <>
          <div className={styles.toolbar}>
            <div className={styles.control}>
              <span>Group by</span>
              <div className={styles.segmented} role="group" aria-label="Group dismissed leads by">
                {DismissedLeadGroupingSchema.options.map(option => (
                  <button
                    key={option}
                    type="button"
                    className={styles.segment}
                    aria-pressed={grouping === option}
                    onClick={() => setGrouping(option)}
                  >
                    {GROUPING_LABELS[option]}
                  </button>
                ))}
              </div>
            </div>
            <div className={styles.control} role="group" aria-label="Show leads dismissed by">
              {(['all', 'verifier', 'triage'] as const).map(option => (
                <button
                  key={option}
                  type="button"
                  className={styles.filterChip}
                  aria-pressed={filter === option}
                  onClick={() => setFilter(option)}
                >
                  {option === 'all' ? 'All' : DISMISSED_BY_LABELS[option]} (
                  {option === 'all' ? leads.length : (counts[option] ?? 0)})
                </button>
              ))}
            </div>
          </div>
          <div className={styles.groups}>
            {groupDismissedLeads(model, visible, grouping).map(group => (
              <section key={group.key} className={styles.group} aria-label={group.title}>
                <div className={styles.groupHeader}>
                  {group.eyebrow && <span className={styles.groupEyebrow}>{group.eyebrow}</span>}
                  <h4 className={styles.groupTitle}>{group.title}</h4>
                  <span className={styles.groupCount}>
                    {group.leads.length} {pluralize(group.leads.length, 'lead', 'leads')}
                  </span>
                </div>
                <ol className={styles.leads}>
                  {group.leads.map(lead => (
                    <DismissedLeadItem
                      key={lead.finding_uuid}
                      lead={lead}
                      where={whereLabel(model, lead, grouping)}
                      isOpen={open.has(lead.finding_uuid)}
                      onToggle={() => toggle(lead.finding_uuid)}
                    />
                  ))}
                </ol>
              </section>
            ))}
          </div>
        </>
      )}
    </section>
  )
}
